/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from "react";
import { useAppSelector } from "../../../app/hooks";
import { Link } from "react-router-dom";
import Icon from "../../../components/icon";
import getInitials from "../../../utils/getInitials";
import Offcanvas from "./offcanvas";
import moment from "moment";
import { Menu } from "./type";

interface Props {
  isOpen: boolean;
  onClosed: any;
  data: Menu[];
}

const MenuParent = ({ id, label }: Menu) => {
  return (
    <li key={id} className="text font-bold text-gray-600">
      {label}
    </li>
  );
};

const MenuItem = ({ id, icon, url, label }: Menu) => {
  return (
    <li key={id} className="w-full flex gap-1.5 items-center py-3">
      <Icon name={icon as string} className="w-5 h-5 text-gray-700" />
      <Link to={url as string}>
        <span className="text-gray-700 text-md font-semibold">{label}</span>
      </Link>
    </li>
  );
};

const MobileMenu = ({ isOpen, onClosed, data }: Props) => {
  const { user } = useAppSelector((state) => state.auth);

  const urlAuth = process.env.REACT_APP_AUTH_SERVICE;

  const handleClose = () => {
    onClosed(true);
  };

  const topNavs = useMemo(() => {
    return data.filter((n) => n.position === "TOP");
  }, [data]);

  const groupNavs = useMemo(() => {
    const systemNav = data.filter((d) => d.label === "System Management");
    if (systemNav.length > 0) {
      return systemNav[0].submenu;
    } else {
      return [];
    }
  }, [data]);

  const navigation = groupNavs?.map((parent) => {
    return (
      <ul key={parent.id} className="pb-5">
        <button onClick={handleClose} className="w-full text-left">
          <MenuParent id={parent.id} label={parent.label} />
        </button>
        {parent.submenu?.map((child) => (
          <button key={child.id} onClick={handleClose} className="w-full justify-left">
            <MenuItem
              key={child.id}
              id={child.id}
              icon={child.icon}
              url={child.url}
              label={child.label}
            />
          </button>
        ))}
      </ul>
    );
  });

  return (
    <Offcanvas
      isOpen={isOpen}
      onClose={onClosed}
      className={`h-screen p-5 ${isOpen ? "block" : "hidden"}`}
    >
      <div className="w-full flex flex-wrap justify-between bg-white">
        <div className="flex items-center gap-1">
          <Link to="/">
            <Icon
              name="arsi"
              className="text-purple w-4 h-4"
              onClick={handleClose}
            />
          </Link>
          <Link to="/">
            <span className="text-gray-700 text-xl text-semibold">arsi.</span>
          </Link>
        </div>
        <Icon
          name="close"
          className="text-gray-400 w-4 h-4"
          onClick={handleClose}
        />
      </div>
      <div className="w-full py-5 border-b border-b-gray-200">
        <div className="flex flex-wrap justify-between">
          <div className="flex gap-3">
            {user?.profilePicture ? (
              <img
                src={`${urlAuth?.replace(
                  /\/$/,
                  ""
                )}/photos/${user?.username.toLowerCase()}/${
                  user?.profilePicture
                }`}
                alt="user"
                className="w-[48px] h-[48px] rounded-full"
              />
            ) : (
              <div className="bg-primary-500 rounded-full w-[48px] h-[48px] flex text-white items-center justify-center text-lg font-semibold">
                <span>{getInitials(user?.fullname ?? "")}</span>
              </div>
            )}
            <div>
              <h6 className="text-gray-700 text-[16px] font-bold">
                {user?.fullname}
              </h6>
              <p className="text-gray-400 text-md font-semibold">
                {user?.tenantName} . {user?.roleDescription}
              </p>
            </div>
          </div>
          <div>
            <Link to="/profile/edit" onClick={handleClose}>
              <span className="flex border border-gray-200 rounded-lg py-2 px-4 gap-1 items-center">
                <Icon name="edit" className="h-3 w-3 font-bold" />
                <span className="font-bold text-gray-700">Edit Profile</span>
              </span>
            </Link>
          </div>
        </div>
        <div className="my-2">
          <div className="flex gap-1 items-center">
            <Icon
              name="female"
              className="text-gray-700 w-5 h-5 font-semibold"
            />
            <span className="text-md font-semibold text-gray-700">
              {user?.gender}
            </span>
          </div>
        </div>
        <div className="my-2">
          <div className="flex gap-1 items-center">
            <Icon
              name="calendar"
              className="text-gray-700 w-5 h-5 font-semibold"
            />
            <span className="text-md font-semibold text-gray-700">
              {moment(user?.dob).format("DD MMM yyyy")} .
            </span>
          </div>
        </div>
        <div className="my-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-2 px-3 py-1 border border-gray-200 rounded-md items-center">
              <Icon name="mail" className="text-gray-400 w-5 h-5" />
              <span className="text-gray-400 text-md font-semibold">
                {user?.username}
              </span>
            </div>
            <div className="flex gap-2 px-3 py-1 border border-gray-200 rounded-md items-center">
              <Icon
                name="phone"
                className="text-gray-400 w-5 h-5 font-semibold"
              />
              <span className="text-gray-400 text-md font-semibold">
                {user?.email}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full mt-5">
        {topNavs.map((nav) => (
          <div key={nav.id} className="w-full flex gap-1.5 items-center py-3">
            <Icon name={nav.icon as string} className="w-5 h-5 text-gray-700" />
            <Link to={nav.url as string} className="w-full">
              <button
                className="text-gray-700 text-md font-semibold w-full text-left"
                onClick={handleClose}
              >
                {nav.label}
              </button>
            </Link>
          </div>
        ))}
        {navigation}
        <div className="flex gap-1 py-3 items-center">
          <Icon name="logout" className="w-5 h-5 text-gray-700" />
          <Link to="/signout">
            <span className="text-gray-700 text-md font-semibold">Log Out</span>
          </Link>
        </div>
      </div>
    </Offcanvas>
  );
};

export default MobileMenu;
